/**
 * Prestige テーマ用パララックス＆フェードイン効果
 * 対象：トップページのセクション
 *
 * 機能：
 * - IntersectionObserverでビューポート内のセクション検出
 * - requestAnimationFrameによるスムーズなパララックス
 * - prefers-reduced-motionによるアクセシビリティ対応
 * - モバイル対応
 */

(function () {
  'use strict';

  console.log('🔍 パララックスデバッグ: スクリプト読み込み開始');

  // 設定値を直接使用
  const SETTINGS = {
    animationDuration: 800,
    animationDelay: 0,
    parallaxStrength: 100,
    mobileEnabled: true
  };

  console.log('🔍 パララックス設定:', SETTINGS);

  // ユーティリティ関数
  const util = {
    // モーション軽減設定の検出
    prefersReducedMotion: () => {
      const result = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      console.log('🔍 モーション軽減設定:', result ? '有効' : '無効');
      return result;
    },

    // モバイル判定
    isMobile: () => {
      const result = window.innerWidth < 769;
      console.log('🔍 モバイル判定:', result ? 'モバイル' : 'デスクトップ', `(${window.innerWidth}px)`);
      return result;
    },

    // トップページ判定 - id="index"で判定
    isIndexPage: () => {
      return document.body.id === 'index';
    },

    // 要素の設定取得 (data属性から)
    getConfig: (el) => {
      return {
        animationDuration: parseInt(el.dataset.animationDuration || SETTINGS.animationDuration, 10),
        animationDelay: parseInt(el.dataset.animationDelay || SETTINGS.animationDelay, 10),
        parallaxStrength: parseInt(el.dataset.parallaxStrength || SETTINGS.parallaxStrength, 10) / 100,
        mobileEnabled: (el.dataset.mobileEnabled || SETTINGS.mobileEnabled) !== 'false',
      };
    },

    // DOM構造チェック
    logDOMStructure: () => {
      const sections = document.querySelectorAll('.shopify-section');
      console.log(`🔍 検出されたセクション数: ${sections.length}`);

      if (sections.length > 0) {
        console.log('🔍 最初のセクションの構造:', sections[0].outerHTML.slice(0, 500) + '...');
      }
    },
  };

  // パララックスコントローラー
  const ParallaxController = {
    sections: [],
    observer: null,
    ticking: false,

    init() {
      console.log('🔍 パララックス初期化開始');

      // トップページ判定
      if (!util.isIndexPage()) {
        console.log('🔍 トップページではないためパララックスは適用しません。body id:', document.body.id);
        return;
      }

      console.log('🔍 トップページを検出しました。body id:', document.body.id);

      // shopify-section-で始まるID要素を検索
      try {
        // shopify-section-で始まるID属性を持つ要素を全て取得
        this.sections = Array.from(document.querySelectorAll('[id^="shopify-section-"]'));
        console.log(`🔍 ショッピファイセクション検出: ${this.sections.length}個`);

        if (this.sections.length === 0) {
          console.warn('🔍 警告: セクションが見つかりませんでした');
          return;
        }
      } catch (e) {
        console.error('🔍 セクション検出エラー:', e);
        return;
      }

      // パララックスクラス適用
      try {
        // パララックス強度を設定
        document.documentElement.style.setProperty('--parallax-amount', '50px');

        this.sections.forEach((section, i) => {
          // ヘッダーやフッターなど特定のセクションは除外
          if (section.id.includes('header') || section.id.includes('footer') || section.id.includes('announcement-bar')) {
            console.log(`🔍 セクション${i + 1} (${section.id})はスキップします`);
            return;
          }

          console.log(`🔍 セクション${i + 1} (${section.id})処理中...`);

          // すでに適用済みの場合はスキップ
          if (section.classList.contains('parallax-applied')) {
            console.log(`🔍 セクション${i + 1}は既に適用済みです`);
            return;
          }

          // メインコンテンツ要素を取得 (最初の直接の子要素)
          const mainContent = section.querySelector(':scope > div, :scope > section, :scope > article');

          if (mainContent) {
            // メインコンテンツにパララックスを適用
            mainContent.classList.add('parallax');
            section.classList.add('parallax-applied'); // 二重適用防止

            console.log(`🔍 セクション${i + 1}のメインコンテンツにパララックス適用: ${mainContent.className}`);
          } else {
            // メインコンテンツが見つからない場合はセクション自体に適用
            section.classList.add('parallax');
            section.classList.add('parallax-applied');

            console.log(`🔍 セクション${i + 1}に直接パララックス適用: ${section.className}`);
          }
        });
      } catch (e) {
        console.error('🔍 パララックス適用エラー:', e);
      }

      // 全てのパララックス要素を取得（メインコンテンツに適用したものも含む）
      const allParallaxElements = document.querySelectorAll('.parallax');
      console.log(`🔍 パララックス適用要素の総数: ${allParallaxElements.length}個`);

      // Intersection Observerの設定
      try {
        this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
          root: null,
          rootMargin: '0px',
          threshold: 0.1,
        });

        // 各パララックス要素を監視
        allParallaxElements.forEach((element) => {
          this.observer.observe(element);
        });

        console.log('🔍 Intersection Observer 設定完了');
      } catch (e) {
        console.error('🔍 Intersection Observer エラー:', e);
      }

      // スクロールイベントのリスナー登録
      if (!util.prefersReducedMotion()) {
        window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
        console.log('🔍 スクロールイベントリスナー設定完了');
      }

      console.log('🔍 パララックス初期化完了');
    },

    // 交差監視のハンドラー
    handleIntersection(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const element = entry.target;

          // 表示フラグを追加
          element.classList.add('parallax--visible');
          console.log('🔍 要素可視化:', element.className);

          // スクロール値の初期化
          this.updateParallaxPosition(element);

          // 一度表示されたら監視を解除（パフォーマンス向上）
          this.observer.unobserve(element);
        }
      });
    },

    // スクロールハンドラー
    handleScroll() {
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          const allParallaxElements = document.querySelectorAll('.parallax--visible');

          allParallaxElements.forEach((element) => {
            this.updateParallaxPosition(element);
          });

          this.ticking = false;
        });

        this.ticking = true;
      }
    },

    // 要素のパララックス位置更新
    updateParallaxPosition(element) {
      // モーション軽減が有効ならスキップ
      if (util.prefersReducedMotion()) return;

      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // ビューポート内での位置（0～1）
      let visibleRatio = 1 - rect.top / (windowHeight + rect.height);
      visibleRatio = Math.max(0, Math.min(1, visibleRatio));

      // スクロール値をCSS変数に設定
      element.style.setProperty('--parallax-scroll', visibleRatio.toFixed(2));
    },
  };

  // 単一初期化を保証
  let initialized = false;

  function initOnce() {
    if (!initialized) {
      initialized = true;
      ParallaxController.init();
    }
  }

  // 初期化タイミング
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOnce);
  } else {
    // 既にDOMが読み込まれている場合は即時実行
    initOnce();
  }

  // ページ完全ロード時に状態確認
  window.addEventListener('load', () => {
    console.log('🔍 ページ完全ロード - 最終状態確認');

    // トップページかどうか確認
    if (!util.isIndexPage()) {
      console.log('🔍 トップページではないため、パララックスは適用されていません');
      return;
    }

    // パララックスクラスが適用されている要素をカウント
    const parallaxElements = document.querySelectorAll('.parallax');
    console.log(`🔍 パララックス適用要素数: ${parallaxElements.length}`);

    // 表示状態になっている要素をカウント
    const visibleElements = document.querySelectorAll('.parallax--visible');
    console.log(`🔍 表示状態要素数: ${visibleElements.length}`);
  });
})();